import { useState, useCallback } from "react";
import type { ITask } from "../types";
import { useStore } from "../store/useStore";
import { getIncompletePrerequisites } from "../lib/dependency-helpers";
import CompletionConfirmModal from "../components/CompletionConfirmModal";

/**
 * Wrap task completion with prerequisite-aware confirmation.
 *
 * If the task being completed has any incomplete prerequisites, shows a modal
 * asking whether to also complete them. Otherwise completes immediately.
 */
export function useConfirmedComplete() {
  const completeTask = useStore((s) => s.completeTask);
  const dependencies = useStore((s) => s.dependencies);
  const tasks = useStore((s) => s.tasks);

  const [pending, setPending] = useState<{
    task: ITask;
    incomplete: ITask[];
  } | null>(null);

  const requestComplete = useCallback(
    (taskUuid: string) => {
      const task = tasks.find((t) => t.uuid === taskUuid);
      if (!task) return;
      const incomplete = getIncompletePrerequisites(taskUuid, dependencies, tasks);
      if (incomplete.length === 0) {
        completeTask(taskUuid);
        return;
      }
      setPending({ task, incomplete });
    },
    [tasks, dependencies, completeTask]
  );

  const modal = pending ? (
    <CompletionConfirmModal
      isOpen={!!pending}
      onClose={() => setPending(null)}
      task={pending.task}
      incompletePrereqs={pending.incomplete}
      onConfirm={(cascadeUuids) => {
        completeTask(pending.task.uuid, cascadeUuids);
      }}
    />
  ) : null;

  return { requestComplete, modal };
}
